import { buildBlockContext } from "@redotech/order-tracking-common/build-block-context";
import { pageLineItemPropertyAllowlist } from "@redotech/order-tracking-common/line-item-property-allowlist";
import { TrackingPageBlockType } from "@redotech/order-tracking-common/tracking-page-definition";
import type { TrackingPageOrder } from "@redotech/order-tracking-common/tracking-page-order-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  TrackingPageAccessMode,
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import {
  getSelectedShipmentWithFallback,
  getShipmentIdFromLocation,
  getShipmentIndex,
  getUnfulfilledLineItems,
} from "./shared/fulfillment-utils";
import { getPreviewOrder } from "./tracking-details/preview-mock-data";

export function pageRoute(
  context: TrackingPageContext,
): "builder" | "portal" | "tracking-code" {
  if (context.environment === TrackingPageEnvironment.BUILDER) {
    return "builder";
  }
  if (
    context.environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
    context.accessMode === TrackingPageAccessMode.TRACKING_CODE_ONLY
  ) {
    return "tracking-code";
  }
  return "portal";
}

// Builder samples fill each allowlisted property, so its `{{ }}` token shows a value.
function withSampleProperties(
  order: TrackingPageOrder,
  names: string[],
): TrackingPageOrder {
  const properties = names.map((name) => ({ name, value: `Sample ${name}` }));
  return {
    ...order,
    shipments: order.shipments.map((shipment) => ({
      ...shipment,
      lineItems: shipment.lineItems.map((item) => ({ ...item, properties })),
    })),
  };
}

/** The Liquid `{{ }}` context a Custom HTML block renders with, framed or inline. */
export function useCustomHtmlContext(blockId: string): Record<string, unknown> {
  const context = useRequiredContext(TrackingPageContext);
  const { i18n } = useTranslation();
  const isBuilder = context.environment === TrackingPageEnvironment.BUILDER;
  const route = pageRoute(context);
  const blocks = context.pageSettings?.blocks;
  const propertyAllowlist = useMemo(
    () => pageLineItemPropertyAllowlist(blocks),
    [blocks],
  );
  const blockIndex = (blocks ?? [])
    .filter((block) => block.type === TrackingPageBlockType.CUSTOM_HTML)
    .findIndex((block) => block._id === blockId);

  // With no order yet, the builder previews the same sample shipment as the
  // tracking-details block, so `{{ }}` tokens show values instead of blanks.
  const order = useMemo(
    () =>
      isBuilder && !context.order
        ? withSampleProperties(getPreviewOrder(), propertyAllowlist)
        : context.order,
    [isBuilder, context.order, propertyAllowlist],
  );

  // The same selection the items and tracking details blocks show.
  const { fulfillmentIndex } = useParams<{ fulfillmentIndex?: string }>();
  const selectedShipmentIndex = useMemo(() => {
    if (!order) {
      return 0;
    }
    const selected = getSelectedShipmentWithFallback(
      order.shipments,
      getShipmentIndex(
        fulfillmentIndex,
        context.environment,
        order,
        getShipmentIdFromLocation(),
      ),
    );
    return selected ? order.shipments.indexOf(selected) : 0;
  }, [context.environment, fulfillmentIndex, order]);

  return useMemo(
    () =>
      order
        ? buildBlockContext(order, {
            lineItemPropertyAllowlist: propertyAllowlist,
            selectedShipmentIndex,
            unfulfilledLineItems: getUnfulfilledLineItems(order),
            page: {
              route,
              locale: i18n.language,
              currency: order.currency,
              blockId,
              blockIndex: blockIndex < 0 ? 0 : blockIndex,
            },
          })
        : { v: 1, page: { route, blockId } },
    [
      blockId,
      blockIndex,
      i18n.language,
      order,
      propertyAllowlist,
      route,
      selectedShipmentIndex,
    ],
  );
}
